// =============================================================================
// VINUM - Utility Functions
// Migrated from Flutter utils with EXACT behavior
// =============================================================================

import { VALIDATION, COLORS } from '../constants';
import type { OrderStatus, CartItem } from '../types';

// -----------------------------------------------------------------------------
// Validation Functions
// Exact match with Flutter util.dart validation
// -----------------------------------------------------------------------------

/**
 * Validate Italian Partita IVA
 * Returns error message or null if valid
 */
export function validatePartitaIVA(value: string): string | null {
  if (!value || value.trim() === '') {
    return 'Inserisci la Partita IVA';
  }
  const cleaned = value.trim().replace(/\s/g, '');
  if (!VALIDATION.partitaIVA.test(cleaned)) {
    return 'La Partita IVA deve essere di 11 cifre';
  }
  return null;
}

/**
 * Validate Italian Partita IVA (optional - allows empty)
 */
export function validatePartitaIVAOptional(value: string | null | undefined): string | null {
  if (!value || value.trim() === '') return null;
  return validatePartitaIVA(value);
}

/**
 * Validate Italian Codice Fiscale
 * Returns error message or null if valid
 */
export function validateCodiceFiscale(value: string): string | null {
  if (!value || value.trim() === '') {
    return 'Inserisci il Codice Fiscale';
  }
  const cleaned = value.trim().toUpperCase();
  if (!VALIDATION.codiceFiscale.test(cleaned)) {
    return 'Codice Fiscale non valido';
  }
  return null;
}

/**
 * Validate Italian Codice Fiscale (optional - allows empty)
 */
export function validateCodiceFiscaleOptional(value: string | null | undefined): string | null {
  if (!value || value.trim() === '') return null;
  return validateCodiceFiscale(value);
}

/**
 * Validate email address
 */
export function validateEmail(value: string): string | null {
  if (!value || value.trim() === '') {
    return 'Inserisci un\'email';
  }
  if (!VALIDATION.email.test(value.trim())) {
    return 'Email non valida';
  }
  return null;
}

/**
 * Validate password
 */
export function validatePassword(value: string): string | null {
  if (!value || value.length < VALIDATION.password.minLength) {
    return `La password deve essere di almeno ${VALIDATION.password.minLength} caratteri`;
  }
  return null;
}

/**
 * Validate password confirmation
 */
export function validatePasswordConfirm(password: string, confirm: string): string | null {
  if (password !== confirm) {
    return 'Le password non coincidono';
  }
  return null;
}

/**
 * Validate required field
 */
export function validateRequired(value: string | null | undefined, fieldName = 'Campo'): string | null {
  if (!value || value.trim() === '') {
    return `${fieldName} obbligatorio`;
  }
  return null;
}

// -----------------------------------------------------------------------------
// Formatting Functions
// Exact match with Flutter formatting
// -----------------------------------------------------------------------------

/**
 * Format price in EUR
 */
export function formatPrice(value: number | null | undefined): string {
  if (value == null || isNaN(value)) {
    return 'Prezzo non disponibile';
  }
  return `€${value.toFixed(2)}`;
}

/**
 * Format date in Italian locale
 */
export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';

  return d.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Format datetime in Italian locale
 */
export function formatDateTime(date: string | Date | null | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';

  return d.toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format number with thousand separators
 */
export function formatNumber(value: number | null | undefined): string {
  if (value == null || isNaN(value)) return '0';
  return value.toLocaleString('it-IT');
}

/**
 * Format phone number
 */
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '';
  // Keep international format as-is
  return phone.startsWith('+') ? phone : `+39 ${phone}`;
}

// -----------------------------------------------------------------------------
// String Utilities
// -----------------------------------------------------------------------------

/**
 * Capitalize first letter
 */
export function capitalize(str: string): string {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Truncate string with ellipsis
 */
export function truncate(str: string, maxLength: number): string {
  if (!str || str.length <= maxLength) return str;
  return str.substring(0, maxLength - 3) + '...';
}

/**
 * Slugify string
 */
export function slugify(str: string): string {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Pick first non-empty value from array
 * Exact match with Flutter _pickFirstNonEmpty
 */
export function pickFirstNonEmpty(values: (string | null | undefined)[]): string | null {
  for (const v of values) {
    const s = (v ?? '').toString().trim();
    if (s !== '') return s;
  }
  return null;
}

// -----------------------------------------------------------------------------
// ID Extraction
// Exact match with Flutter _extractItemIdFromAny
// -----------------------------------------------------------------------------

export function extractItemId(obj: Record<string, unknown>): string {
  const candidates = [
    obj['id'],
    obj['itemId'],
    obj['itemID'],
    obj['itemCode'],
    obj['code'],
    obj['ItemId'],
    obj['ItemID'],
    obj['ItemCode'],
    obj['Code'],
  ].filter((e) => e != null && e.toString() !== '');

  return candidates.length > 0 ? candidates[0]!.toString() : '';
}

export function extractWineryId(obj: Record<string, unknown>): string | null {
  const direct = obj['wineryId'];
  if (direct != null && direct.toString() !== '') {
    return direct.toString();
  }
  const w = obj['winery'];
  if (w && typeof w === 'object' && 'id' in w) {
    const winery = w as Record<string, unknown>;
    if (winery['id'] != null && winery['id'].toString() !== '') {
      return winery['id'].toString();
    }
  }
  return null;
}

// -----------------------------------------------------------------------------
// Order Status Utilities
// -----------------------------------------------------------------------------

export function getOrderStatusColor(status: OrderStatus): string {
  return COLORS.orderStatus[status] || '#6B7280';
}

export function getOrderStatusBgColor(status: OrderStatus, isDark: boolean): string {
  const baseColor = getOrderStatusColor(status);
  return isDark ? `${baseColor}33` : `${baseColor}1A`; // 20% or 10% opacity
}

// -----------------------------------------------------------------------------
// Cart Utilities
// -----------------------------------------------------------------------------

export function calculateCartTotal(items: CartItem[]): number {
  return items.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);
}

export function calculateCartItemCount(items: CartItem[]): number {
  return items.reduce((sum, item) => sum + item.quantity, 0);
}

// -----------------------------------------------------------------------------
// Dialog Width Utility
// Exact match with Flutter dialogWidth
// -----------------------------------------------------------------------------

export function getDialogWidth(width: number): number {
  if (width < 480) return width * 0.9;
  if (width < 800) return 400;
  return 500;
}

// -----------------------------------------------------------------------------
// Password Strength
// Exact match with Flutter password strength indicator
// -----------------------------------------------------------------------------

export interface PasswordStrength {
  score: number; // 0-4
  label: string;
  color: string;
}

export function getPasswordStrength(password: string): PasswordStrength {
  if (!password) {
    return { score: 0, label: 'Molto debole', color: COLORS.error };
  }

  let score = 0;

  // Length checks
  if (password.length >= 6) score++;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;

  // Character variety
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;

  // Normalize to 0-4
  score = Math.min(4, Math.floor(score * 4 / 6));

  const levels: PasswordStrength[] = [
    { score: 0, label: 'Molto debole', color: COLORS.error },
    { score: 1, label: 'Debole', color: COLORS.warning },
    { score: 2, label: 'Discreta', color: COLORS.warning },
    { score: 3, label: 'Buona', color: COLORS.success },
    { score: 4, label: 'Ottima', color: COLORS.success },
  ];

  return levels[score];
}

// -----------------------------------------------------------------------------
// Debounce & Throttle
// -----------------------------------------------------------------------------

export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;

  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

export function throttle<T extends (...args: unknown[]) => unknown>(
  fn: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle = false;

  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      fn(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

// -----------------------------------------------------------------------------
// Local Storage Helpers (Web)
// -----------------------------------------------------------------------------

export function safeJsonParse<T>(json: string | null, fallback: T): T {
  if (!json) return fallback;
  try {
    return JSON.parse(json) as T;
  } catch {
    return fallback;
  }
}

export function safeJsonStringify(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return '';
  }
}

// -----------------------------------------------------------------------------
// Unique ID Generator
// -----------------------------------------------------------------------------

export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

// -----------------------------------------------------------------------------
// Deep Clone
// -----------------------------------------------------------------------------

export function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

// -----------------------------------------------------------------------------
// Object Utilities
// -----------------------------------------------------------------------------

export function omit<T extends Record<string, unknown>, K extends keyof T>(
  obj: T,
  keys: K[]
): Omit<T, K> {
  const result = { ...obj };
  keys.forEach((key) => delete result[key]);
  return result;
}

export function pick<T extends Record<string, unknown>, K extends keyof T>(
  obj: T,
  keys: K[]
): Pick<T, K> {
  const result = {} as Pick<T, K>;
  keys.forEach((key) => {
    if (key in obj) {
      result[key] = obj[key];
    }
  });
  return result;
}

// -----------------------------------------------------------------------------
// Array Utilities
// -----------------------------------------------------------------------------

export function groupBy<T>(array: T[], key: keyof T): Record<string, T[]> {
  return array.reduce((groups, item) => {
    const groupKey = String(item[key]);
    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(item);
    return groups;
  }, {} as Record<string, T[]>);
}

export function sortBy<T>(array: T[], key: keyof T, order: 'asc' | 'desc' = 'asc'): T[] {
  return [...array].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];

    if (aVal < bVal) return order === 'asc' ? -1 : 1;
    if (aVal > bVal) return order === 'asc' ? 1 : -1;
    return 0;
  });
}

export function uniqueBy<T>(array: T[], key: keyof T): T[] {
  const seen = new Set();
  return array.filter((item) => {
    const val = item[key];
    if (seen.has(val)) return false;
    seen.add(val);
    return true;
  });
}

// -----------------------------------------------------------------------------
// Storage Configuration (re-exported from storage.ts)
// -----------------------------------------------------------------------------

export { configureStorage, getStorage, createZustandStorage } from './storage';
export type { StorageAdapter } from './storage';

// -----------------------------------------------------------------------------
// Role Hierarchy Utilities (re-exported from roles.ts)
// -----------------------------------------------------------------------------

export {
  ROLE_HIERARCHY,
  DEFAULT_ROLE_PRIORITY,
  ROLE_NAMES_BY_PRIORITY,
  getRolePriority,
  getHighestRole,
  hasAtLeastRole,
  hasExactHighestRole,
  hasRole,
  isOnlyRole,
  getAccessibleRoles,
  compareRoles,
  sortRolesByPriority,
  canAccessAdmin,
  canAccessManager,
  getRoleLabel,
  getRoleBadgeColor,
  isCustomer,
  isStaffMember,
} from './roles';
export type { RoleName, RoleObject } from './roles';
